import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { errText } from "../../lib/api";
import { Button } from "../ui/button";
import { DotsSixVertical, ArrowCounterClockwise, FloppyDisk } from "@phosphor-icons/react";

// Human-friendly labels for each tab id. Keep in sync with Portal.jsx TABS_FULL.
const TAB_LABELS = {
  overview: "Dashboard",
  tax: "Tax & Filings",
  accounting: "Accounting & Financials",
  advisory: "Advisory & CFO",
  ai: "AI & Automation",
  documents: "Documents & Vault",
  billing: "Billing & Engagements",
  messages: "Communication & Messages",
  support: "Support & Requests",
};

// Firm-side drag-reorder chips. Persists to /api/clients/{id}/tab-order.
// Uses HTML5 drag-and-drop to match the pattern used by the Kanban board — no
// extra library required.
export default function PortalTabOrderPanel({ clientId, clientName }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["client-tab-order", clientId],
    queryFn: () => api.get(`/clients/${clientId}/tab-order`).then((r) => r.data),
    staleTime: 30000,
  });
  const [order, setOrder] = useState([]);
  const [dragId, setDragId] = useState(null);

  useEffect(() => {
    if (data?.tab_order) setOrder(data.tab_order);
  }, [data]);

  const save = useMutation({
    mutationFn: () => api.put(`/clients/${clientId}/tab-order`, { tab_order: order }),
    onSuccess: () => {
      toast.success(`Saved — ${clientName || "client"} will see this tab order on next portal load`);
      qc.invalidateQueries({ queryKey: ["client-tab-order", clientId] });
    },
    onError: (e) => toast.error(errText(e)),
  });

  const reset = () => {
    if (data?.default) setOrder(data.default);
  };

  const onDragStart = (id) => (e) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOver = (id) => (e) => {
    e.preventDefault();
    if (!dragId || dragId === id) return;
    setOrder((cur) => {
      const from = cur.indexOf(dragId);
      const to = cur.indexOf(id);
      if (from === -1 || to === -1) return cur;
      const next = [...cur];
      next.splice(from, 1);
      next.splice(to, 0, dragId);
      return next;
    });
  };
  const onDragEnd = () => setDragId(null);

  const dirty = data?.tab_order && JSON.stringify(order) !== JSON.stringify(data.tab_order);

  if (isLoading) return <p className="text-xs text-zinc-400" data-testid="tab-order-loading">Loading tab order…</p>;

  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-5" data-testid="portal-tab-order-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Portal tab order</h3>
          <p className="text-[11px] text-zinc-500 mt-0.5">Drag to reorder how {clientName || "this client"} sees their dashboard tabs. The category tabs (Tax / Accounting / Advisory / AI) still only render if there's an active engagement in that category.</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="tab-order-reset-btn" onClick={reset}
            className="flex items-center gap-1 text-[11px] text-zinc-600 hover:text-slate-900 border border-zinc-200 rounded-md px-2.5 py-1.5 hover:border-slate-900 transition-colors duration-150">
            <ArrowCounterClockwise size={12} /> Reset
          </button>
          <Button data-testid="tab-order-save-btn" size="sm" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
            <FloppyDisk size={13} className="mr-1" /> {save.isPending ? "Saving…" : dirty ? "Save order" : "Saved"}
          </Button>
        </div>
      </div>
      <ul className="space-y-1.5" data-testid="tab-order-list">
        {order.map((id, i) => (
          <li key={id}
              draggable
              onDragStart={onDragStart(id)}
              onDragOver={onDragOver(id)}
              onDragEnd={onDragEnd}
              data-testid={`tab-order-row-${id}`}
              className={`flex items-center gap-3 rounded-md border px-3 py-2 bg-white cursor-move transition-all duration-150 ${dragId === id ? "border-slate-900 shadow-md opacity-70" : "border-zinc-200 hover:border-slate-900 hover:shadow-sm"}`}>
            <DotsSixVertical size={16} className="text-zinc-400 shrink-0" />
            <span className="text-[10px] font-mono text-zinc-400 w-5 text-right">{i + 1}</span>
            <span className="text-sm text-slate-900 flex-1">{TAB_LABELS[id] || id}</span>
            <span className="text-[9px] uppercase tracking-wider text-zinc-400 font-mono">{id}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
